import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function normalizePhone(value) {
  const digits = String(value || '').replace(/\D/g, '');

  // Norsk nummer skrevet med landskode, f.eks. 4798765432
  if (digits.length === 10 && digits.startsWith('47')) {
    return digits.slice(2);
  }

  // Behold vanlige 8-sifrede nummer urørt
  return digits;
}

// onCreated (valgfri): kalles med den nye kunden i stedet for å åpne kundekortet (brukes i Ny kundesamhandling).
// forslag (valgfri): { firstName, lastName, email } skjemaet starter med, f.eks. fra et søk uten treff.
// Telefon kommer fortsatt via phoneNumber.
function CustomerForm({ addCustomer, customers, phoneNumber, setSearchQuery, onCreated, forslag }) {
  const [formData, setFormData] = useState({
    firstName: forslag?.firstName || '',
    lastName: forslag?.lastName || '',
    phoneNumber: '',
    email: forslag?.email || '',
    customerNumber: '',
    status: 'aktiv'
  });

  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      phoneNumber: phoneNumber || ''
    }));
  }, [phoneNumber]);

  useEffect(() => {
    const validCustomers = customers.filter(
      (c) => typeof c.customerNumber === 'number' && !isNaN(c.customerNumber)
    );

    const nextCustomerNumber =
      validCustomers.length > 0
        ? Math.max(...validCustomers.map((c) => c.customerNumber)) + 1
        : 1;

    setFormData((prevData) => ({
      ...prevData,
      customerNumber: nextCustomerNumber
    }));
  }, [customers]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: value
    }));

    if (name === 'phoneNumber') {
      const normalizedValue = normalizePhone(value);

      // Send søk videre mens brukeren skriver
      if (normalizedValue.length > 0) {
        setSearchQuery(normalizedValue);
      } else {
        setSearchQuery('');
      }

      // Duplikatsjekk kun når telefonnummer er komplett
      if (normalizedValue.length === 8) {
        const existingCustomer = customers.find(
          (customer) =>
            normalizePhone(customer.phoneNumber) === normalizedValue
        );

        if (existingCustomer) {
          setErrorMessage('Kunde er allerede registrert.');
        } else {
          setErrorMessage('');
        }
      } else {
        setErrorMessage('');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const normalizedPhone = normalizePhone(formData.phoneNumber);

    if (normalizedPhone.length !== 8 || !/^\d{8}$/.test(normalizedPhone)) {
      setErrorMessage('Telefonnummer må være nøyaktig 8 sifre.');
      return;
    }

    const existingCustomer = customers.find(
      (customer) => normalizePhone(customer.phoneNumber) === normalizedPhone
    );

    if (existingCustomer) {
      setErrorMessage('Kunde er allerede registrert.');
      setSearchQuery(normalizedPhone);
      return;
    }

    if (errorMessage) {
      return;
    }

    const trimmedEmail = formData.email.trim();
    if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setErrorMessage('E-post må enten stå tomt eller være en gyldig adresse (f.eks. navn@eksempel.no).');
      return;
    }

    const newCustomer = {
      ...formData,
      phoneNumber: normalizedPhone,
      email: trimmedEmail,
      registrationDate: new Date().toLocaleString('no-NO', {
        timeZone: 'Europe/Oslo'
      }),
      lastModified: '',
    };

    try {
      const response = await fetch(`${API_BASE_URL}/customers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newCustomer),
      });

      if (response.ok) {
        const addedCustomer = await response.json();
        addCustomer(addedCustomer);
        if (onCreated) onCreated(addedCustomer);
        else navigate(`/customer-details/${addedCustomer._id}`);
      } else {
        const err = await response.json().catch(() => ({}));
        setErrorMessage(err.message || 'Feil ved registrering av kunde.');
      }
    } catch (error) {
      console.error('Feil ved kommunikasjon med serveren:', error);
      setErrorMessage('Kunne ikke nå serveren.');
    }
  };

  return (
    <div className="max-w-5xl mx-auto py-8 bg-white shadow-lg rounded-lg p-6">
      <h2 className="text-3xl font-bold mb-6 text-center text-gray-800">
        Registrer Ny Kunde
      </h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="kundeskjema-firstName" className="block text-sm font-medium text-gray-700">
            Fornavn
          </label>
          <input
            id="kundeskjema-firstName"
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            required
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"
          />
        </div>

        <div>
          <label htmlFor="kundeskjema-lastName" className="block text-sm font-medium text-gray-700">
            Etternavn
          </label>
          <input
            id="kundeskjema-lastName"
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            required
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"
          />
        </div>

        <div>
          <label htmlFor="kundeskjema-phoneNumber" className="block text-sm font-medium text-gray-700">
            Telefonnummer
          </label>
          {errorMessage && (
            <p role="alert" className="text-red-700 text-sm mt-1 mb-1">{errorMessage}</p>
          )}
          <input
            id="kundeskjema-phoneNumber"
            type="tel"
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleChange}
            required
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"
          />
        </div>

        <div>
          <label htmlFor="kundeskjema-email" className="block text-sm font-medium text-gray-700">
            E-post
          </label>
          <input
            id="kundeskjema-email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"
          />
        </div>

        <div className="text-center">
          <button
            type="submit"
            className="bg-green-700 text-white px-6 py-2 rounded hover:bg-green-800"
          >
            Registrer Kunde
          </button>
        </div>
      </form>
    </div>
  );
}

export default CustomerForm;